import { CheckCircle2, Circle, CircleAlert } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { DateValue, StatusBadge } from '@/components/common'
import { PRO_STATUS_LABEL, PRO_STATUS_TONE, type ProCaseDecisionContext } from '@/features/pro'

/**
 * The case, with what is still outstanding called out separately.
 *
 * <p>The guard refuses approval on the outstanding list, so it gets its own block rather than
 * being something an approver has to work out by comparing two columns of ticks.
 */
export function ProCaseContext({ detail }: { detail: ProCaseDecisionContext }) {
  const { proCase } = detail

  return (
    <div className="space-y-4">
      {detail.outstandingChecklist.length > 0 && (
        <Card className="border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-danger)]">
            <CircleAlert className="size-4" aria-hidden="true" />
            Approving this will be refused
          </h3>
          <ul className="mt-2 list-inside list-disc text-sm">
            {detail.outstandingChecklist.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">
            {proCase.caseNo} · {proCase.templateName}
          </h3>
          <StatusBadge
            status={proCase.status}
            tone={PRO_STATUS_TONE[proCase.status]}
            label={PRO_STATUS_LABEL[proCase.status]}
          />
        </div>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          {proCase.employeeName ?? proCase.candidateRef ?? 'The company'} · opened{' '}
          <DateValue value={proCase.openedOn} />
          {proCase.targetDate && (
            <>
              {' '}· due <DateValue value={proCase.targetDate} />
              {proCase.overdue && (
                <span className="ms-1 font-medium text-[var(--color-danger)]">overdue</span>
              )}
            </>
          )}
        </p>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Steps</h3>
        <ol className="mt-2 space-y-1.5 text-sm">
          {detail.steps.map((step) => (
            <li key={step.id} className="flex flex-wrap items-center gap-2">
              {step.status === 'DONE' ? (
                <CheckCircle2 className="size-4 text-[var(--color-success)]" aria-hidden="true" />
              ) : (
                <Circle className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
              )}
              <span className="text-[var(--color-heading)]">{step.name}</span>
              <StatusBadge status={step.status} />
              {step.expectedDays != null && (
                <span className="text-xs text-[var(--color-muted)]">
                  expected {step.expectedDays} d
                  {step.actualDays != null && ` · took ${step.actualDays} d`}
                </span>
              )}
            </li>
          ))}
        </ol>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Cost so far</h3>
        {detail.costs.length === 0 ? (
          <p className="mt-2 text-sm text-[var(--color-muted)]">Nothing recorded yet.</p>
        ) : detail.costSoFar == null ? (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            {detail.costs.length} entries in more than one currency — they are not totalled,
            because a sum across currencies looks like money and is not.
          </p>
        ) : (
          <p className="mt-2 text-sm tabular-nums text-[var(--color-heading)]">
            {detail.costSoFar.toFixed(2)} {detail.costCurrency} over {detail.costs.length}{' '}
            {detail.costs.length === 1 ? 'entry' : 'entries'}
          </p>
        )}
      </Card>
    </div>
  )
}
